import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  EntitlementUsage,
  useEntitlement,
  useEntitlementUpgradeAction,
} from '@features/entitlements';
// Deep import (not the @features/organizations barrel) to avoid pulling the
// org page graph into the integrations card.
import { usePracticePlanDestination } from '@features/organizations/store/hooks';
import { useSubscription } from '@features/subscriptions';
import { useUserRoles } from '@features/user';
import {
  AllInclusive,
  ArrowForward,
  CardGiftcard,
  CheckCircle,
  InfoOutlined,
  Launch,
  PersonAdd,
  PlayCircleOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Link,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEventTracking, useFeatureGate } from '@services/statsig';
import { DynamicConfigs, FeatureGates } from '@services/statsig/constants';
import { useStatsigClient } from '@statsig/react-bindings';
import { publicAsset } from '@utils/publicAsset';
import { ROLES } from '@utils/roleUtils';

const DEFAULT_CHROME_WEB_STORE_URL =
  'https://chromewebstore.google.com/detail/jotpsych-ehr-extension/jooogmdiklogejmlncnknjembkilajdb';

function useEhrExtensionStoreUrl(): string {
  const { client } = useStatsigClient();
  return useMemo(() => {
    const config = client.getDynamicConfig(DynamicConfigs.EHR_EXTENSION);
    const url = config.value?.chrome_web_store_url;
    return typeof url === 'string' && url.length > 0 ? url : DEFAULT_CHROME_WEB_STORE_URL;
  }, [client]);
}

function useEhrExtensionUrls() {
  const { client } = useStatsigClient();
  return useMemo(() => {
    const config = client.getDynamicConfig(DynamicConfigs.EHR_EXTENSION_URLS);
    const value = config.value as Partial<{ howToUrl: string }>;
    return {
      howToUrl:
        typeof value.howToUrl === 'string' && value.howToUrl.length > 0
          ? value.howToUrl
          : null,
    };
  }, [client]);
}

const CAPABILITY_NAME = 'ehr_note_copy';

type ViewState = 'not-installed' | 'installed-free' | 'installed-unlimited';

type Entitlement = ReturnType<typeof useEntitlement>['entitlement'];

/**
 * True when the entitlement exists and grants unlimited access (no quota).
 */
function isUnlimitedEntitlement(entitlement: Entitlement): boolean {
  return !!entitlement && (entitlement.quota === null || entitlement.quota === undefined);
}

/**
 * Determines which view to render based on entitlement state.
 *
 * Uses `current_usage > 0` as the sole proxy for "has installed and used the
 * extension". A user with unlimited access (trial or paid) who hasn't made any
 * transfers yet still sees the install prompt — just with adapted copy.
 */
function getViewState(entitlement: Entitlement): ViewState {
  if (!entitlement) return 'not-installed';
  const hasUsage = (entitlement.current_usage ?? 0) > 0;
  if (!hasUsage) return 'not-installed';
  return isUnlimitedEntitlement(entitlement) ? 'installed-unlimited' : 'installed-free';
}

const VALUE_PROPS = [
  {
    title: 'Side-by-side view',
    description: 'See your notes and EHR side-by-side',
  },
  {
    title: 'One-click transfer',
    description: 'One-click transfer — no more switching tabs',
  },
  {
    title: 'Multi-EHR support',
    description: 'Works with SimplePractice, DrChrono, and other EHRs',
  },
];


const HOW_TO_STEPS: Array<{ text: string; tooltip?: string }> = [
  { text: 'Create your note in JotPsych as usual' },
  { text: "Open your EHR's note entry screen" },
  { text: 'Open JotTransfer from your Chrome toolbar' },
  {
    text: 'Select your note and transfer it into your EHR',
    tooltip:
      "JotTransfer matches sections by name. If your JotPsych section names don't match your EHR's field names, you can still copy sections manually from the side panel — no tab-switching required.",
  },
];

/**
 * View shown when user hasn't installed the extension (no entitlement).
 */
const NotInstalledView: React.FC<{
  quota: number | null | undefined;
  isUnlimited: boolean;
}> = ({ quota, isUnlimited }) => {
  const chromeWebStoreUrl = useEhrExtensionStoreUrl();
  const { howToUrl } = useEhrExtensionUrls();
  const { subscription } = useSubscription();
  const { ehrExtension } = useEventTracking();
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const isTrialing = subscription?.subscription_status === 'trialing';
  const freeQuota = quota ?? 15;

  // Banner copy: unlimited takes precedence when paired with a trial or paid addon
  let bannerTitle: string;
  let bannerSubtitle: string;
  if (isUnlimited && isTrialing) {
    bannerTitle = 'Unlimited during your trial';
    bannerSubtitle = 'Transfer notes directly into your EHR while you explore JotPsych';
  } else if (isUnlimited) {
    bannerTitle = 'Unlimited transfers';
    bannerSubtitle = 'Install JotTransfer to start transferring notes to your EHR';
  } else if (isTrialing) {
    bannerTitle = `Included in your trial — ${freeQuota} notes`;
    bannerSubtitle = 'Transfer notes directly into your EHR while you explore JotPsych';
  } else {
    bannerTitle = `Start free with ${freeQuota} notes`;
    bannerSubtitle = 'Try the extension at no cost — upgrade anytime for unlimited use';
  }

  const handleAddToChrome = () => {
    ehrExtension.chromeStoreClicked({ source: 'settings_not_installed' });
    window.open(chromeWebStoreUrl, '_blank', 'noopener,noreferrer');
  };

  const handleWatchDemo = () => {
    if (!howToUrl) return;
    ehrExtension.tutorialClicked({ source: 'settings_not_installed' });
    window.open(howToUrl, '_blank', 'noopener,noreferrer');
  };

  return (
  <Stack spacing={3}>
    {/* CTA banner */}
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2,
        bgcolor: 'grey.100',
        borderRadius: 2,
        px: 3,
        py: 2,
      }}
    >
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
        <CardGiftcard sx={{ fontSize: 24, color: 'grey.600' }} />
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {bannerTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {bannerSubtitle}
          </Typography>
        </Box>
      </Stack>
      <Stack
        direction="row"
        spacing={1}
        sx={{alignItems: "center",  flexWrap: 'wrap', rowGap: 1 }}
      >
        {howToUrl && (
          <Button
            variant="text"
            startIcon={<PlayCircleOutlined />}
            onClick={handleWatchDemo}
            sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            {isSmUp ? 'Watch demo' : 'Demo'}
          </Button>
        )}
        <Button
          variant="contained"
          endIcon={<ArrowForward />}
          onClick={handleAddToChrome}
          sx={{ borderRadius: 6, textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
        >
          Add to Chrome
        </Button>
      </Stack>
    </Box>

    {/* How-to gif */}
    <Box
      component="img"
      src={publicAsset('/new-feature-images/ehr-extension-preview.gif')}
      alt="How to open JotTransfer"
      sx={{
        width: '100%',
        objectFit: 'contain',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    />

    {/* Value props grid */}
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
        gap: 3,
      }}
    >
      {VALUE_PROPS.map(prop => (
        <Card
          key={prop.title}
          variant="outlined"
          sx={{ p: 2.5, textAlign: 'center' }}
        >
          <CheckCircle sx={{ fontSize: 28, color: 'success.main', mb: 1.5 }} />
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            {prop.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {prop.description}
          </Typography>
        </Card>
      ))}
    </Box>
  </Stack>
  );
};

/**
 * How-to steps + optional tutorial video link, shared between free and unlimited views.
 */
const HowToUseSection: React.FC<{
  howToUrl: string | null;
  tutorialClickSource: string;
}> = ({ howToUrl, tutorialClickSource }) => {
  const { ehrExtension } = useEventTracking();
  const handleTutorialClick = () => {
    ehrExtension.tutorialClicked({ source: tutorialClickSource });
  };
  return (
  <Box>
    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
      How to use
    </Typography>
    <Stack spacing={1.5}>
      {HOW_TO_STEPS.map((step, i) => (
        <Stack key={i} direction="row" spacing={1.5} sx={{ alignItems: "flex-start" }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              bgcolor: 'action.selected',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {i + 1}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} sx={{alignItems: "center",  flex: 1, minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              {step.text}
            </Typography>
            {step.tooltip && (
              <Tooltip title={step.tooltip} arrow placement="top">
                <InfoOutlined
                  sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help', flexShrink: 0 }}
                  aria-label="More info"
                />
              </Tooltip>
            )}
          </Stack>
        </Stack>
      ))}
      {howToUrl && (
        <Stack direction="row" spacing={0.75} sx={{alignItems: "center",  pt: 0.5 }}>
          <PlayCircleOutlined sx={{ fontSize: 18, color: 'primary.main' }} />
          <Link
            href={howToUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            underline="hover"
            onClick={handleTutorialClick}
          >
            Watch tutorial video
          </Link>
        </Stack>
      )}
    </Stack>
  </Box>
  );
};

/**
 * View shown when user has a metered (free tier) entitlement.
 */
const InstalledFreeView: React.FC<{ bundleId?: string }> = ({ bundleId }) => {
  const { howToUrl } = useEhrExtensionUrls();
  const { ehrExtension } = useEventTracking();
  const roles = useUserRoles();
  const isPracticeAdmin = roles.includes(ROLES.PRACTICE_ADMINISTRATOR);
  const { handleUpgradeClick } = useEntitlementUpgradeAction({
    capabilityName: CAPABILITY_NAME,
    bundleId,
    capabilityDisplayName: 'JotTransfer',
  });

  const handleUpgrade = () => {
    ehrExtension.addToPlanClicked({ source: 'settings_free_tier_banner_admin' });
    handleUpgradeClick();
  };

  const banner = isPracticeAdmin
    ? {
        Icon: AllInclusive,
        title: 'Go unlimited with JotTransfer',
        description: 'Transfer as many notes as you want with no monthly cap.',
      }
    : {
        Icon: PersonAdd,
        title: 'Want unlimited JotTransfers?',
        description:
          'To upgrade your subscription plan, please reach out to your practice administrator.',
      };
  const { Icon: BannerIcon } = banner;

  return (
    <Stack spacing={3}>
      {/* Usage meter — hide inline upgrade link; the banner below is the primary CTA */}
      <EntitlementUsage
        capabilityName={CAPABILITY_NAME}
        source="ehr_extension_card"
        showUpgradeLink={false}
      />

      {/* Prominent upgrade CTA */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          bgcolor: 'primary.50',
          border: '1px solid',
          borderColor: 'primary.100',
          borderRadius: 2,
          px: 3,
          py: 2,
        }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <BannerIcon sx={{ fontSize: 24, color: 'primary.main' }} />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {banner.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {banner.description}
            </Typography>
          </Box>
        </Stack>
        {isPracticeAdmin && (
          <Button
            variant="contained"
            endIcon={<ArrowForward />}
            onClick={handleUpgrade}
            sx={{ borderRadius: 6, textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            Upgrade to unlimited
          </Button>
        )}
      </Box>

      {/* How to use */}
      <HowToUseSection howToUrl={howToUrl} tutorialClickSource="settings_installed_free" />
    </Stack>
  );
};

/**
 * View shown when user has an unlimited entitlement (paid).
 * Practice admins see a CTA to manage clinicians; non-admins see no CTA.
 */
const InstalledUnlimitedView: React.FC = () => {
  const navigate = useNavigate();
  const roles = useUserRoles();
  const isPracticeAdmin = roles.includes(ROLES.PRACTICE_ADMINISTRATOR);
  const { ehrExtension } = useEventTracking();
  const { howToUrl } = useEhrExtensionUrls();
  const planDestination = usePracticePlanDestination();

  const handleManageClinicians = () => {
    ehrExtension.manageCliniciansClicked();
    navigate(planDestination.route);
  };

  return (
    <Stack spacing={3}>
      {/* Status banner */}
      <Alert
        severity="success"
        icon={<CheckCircle />}
        action={
          <Chip label="Unlimited" size="small" color="success" />
        }
      >
        JotTransfer is installed with unlimited transfers
      </Alert>

      {/* How to use */}
      <HowToUseSection howToUrl={howToUrl} tutorialClickSource="settings_installed_unlimited" />

      {/* Manage subscription — only for practice admins */}
      {isPracticeAdmin && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            px: 2.5,
            py: 1.5,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            Manage your subscription in the Your Practice tab
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Launch />}
            onClick={handleManageClinicians}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            {planDestination.ctaLabel}
          </Button>
        </Box>
      )}
    </Stack>
  );
};

/**
 * EHR Extension integration card for the Account Settings > Integrations page.
 *
 * Shows one of three views based on entitlement state:
 * - NotInstalledView: preview + value props + CTA to install
 * - InstalledFreeView: usage meter + upgrade CTA + how-to
 * - InstalledUnlimitedView: usage stats + how-to
 *
 * Includes a demo state switcher footer for QA testing.
 */
export const EhrExtensionCard: React.FC = () => {
  const isGateEnabled = useFeatureGate(FeatureGates.EHR_EXTENSION_ADDON);
  const { entitlement, isLoading } = useEntitlement(CAPABILITY_NAME);
  const { ehrExtension } = useEventTracking();
  const [demoOverride, setDemoOverride] = useState<ViewState | null>(null);

  const naturalView = getViewState(entitlement);
  const activeView = demoOverride ?? naturalView;

  useEffect(() => {
    if (isGateEnabled && !isLoading) {
      ehrExtension.settingsViewed({ view_state: naturalView });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGateEnabled, isLoading]);

  if (!isGateEnabled) {
    return null;
  }

  if (isLoading) {
    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <Typography color="text.secondary">Loading...</Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
      {/* Header */}
      <Typography variant="h6" gutterBottom>
        JotTransfer
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Box>
        {/* Active view */}
        {activeView === 'not-installed' && (
          <NotInstalledView
            quota={entitlement?.quota}
            isUnlimited={isUnlimitedEntitlement(entitlement)}
          />
        )}
        {activeView === 'installed-free' && (
          <InstalledFreeView bundleId={entitlement?.bundle_id} />
        )}
        {activeView === 'installed-unlimited' && <InstalledUnlimitedView />}
      </Box>

      {/* Demo state switcher for QA — dev only */}
      {import.meta.env.DEV && <Box
        sx={{
          pt: 2,
          mt: 2,
          borderTop: '1px dashed',
          borderColor: 'divider',
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Demo: Preview different states
        </Typography>
        <Stack direction="row" spacing={1}>
          {([
            { value: null, label: `Live (${naturalView})` },
            { value: 'not-installed' as const, label: 'Not installed' },
            { value: 'installed-free' as const, label: 'Free tier' },
            { value: 'installed-unlimited' as const, label: 'Unlimited' },
          ] as const).map(option => (
            <Button
              key={option.label}
              size="small"
              variant={demoOverride === option.value ? 'contained' : 'outlined'}
              onClick={() => setDemoOverride(option.value)}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              {option.label}
            </Button>
          ))}
        </Stack>
      </Box>}
    </Card>
  );
};

EhrExtensionCard.displayName = 'EhrExtensionCard';
